import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiCheck, FiShield, FiClock, FiFileText, FiAlertCircle } = FiIcons;

const Landing = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 flex flex-col">
      {/* Navbar */}
      <nav className="p-6 flex justify-between items-center max-w-6xl mx-auto w-full">
        <div className="font-bold text-2xl text-blue-800 tracking-tight">BOEResumido.com</div>
        <div className="space-x-6 text-sm">
          <Link to="/login" className="text-gray-600 hover:text-blue-800 font-medium transition-colors">Entrar</Link>
          <Link to="/register" className="bg-blue-800 text-white px-5 py-2.5 rounded-md font-medium hover:bg-blue-900 transition-colors shadow-sm">Suscribirse Gratis</Link>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24 text-center">
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 rounded-full text-xs font-medium mb-6">
          <SafeIcon icon={FiAlertCircle} className="w-3 h-3" />Servicio privado e independiente. Resumimos el BOE y te lo mandamos al email diariamente y gratis.</div>
        
        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight">
          Tu resumen matinal del BOE.<br />
          <span className="text-blue-700">Solo normativa. Sin ruido.</span>
        </h1>
        <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
          Recibe cada mañana a las 08:00 un email conciso con las leyes, reales decretos y órdenes que afectan a tu práctica jurídica.
        </p>
        
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 text-sm text-gray-600 mb-10">
          <div className="flex items-center"><SafeIcon icon={FiClock} className="mr-2 text-blue-600" /> Lectura en menos de 2 minutos</div>
          <div className="flex items-center"><SafeIcon icon={FiCheck} className="mr-2 text-blue-600" /> Filtrado exclusivo de novedades normativas</div>
          <div className="flex items-center"><SafeIcon icon={FiShield} className="mr-2 text-blue-600" /> 100% Gratuito</div>
        </div>

        <Link to="/register" className="inline-flex items-center text-lg bg-blue-800 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-900 transition-all hover:-translate-y-1 shadow-lg shadow-blue-100">
          <SafeIcon icon={FiMail} className="mr-3" />
          Recibir el resumen diario
        </Link>
        <p className="mt-4 text-xs text-gray-500">Únete a otros compañeros del sector. Darse de baja es inmediato.</p>
      </main>

      {/* Problem / Solution Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">El BOE tiene cientos de páginas. Tú necesitas leer dos.</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Revisar el Boletín Oficial del Estado cada mañana es una tarea ineficiente pero obligatoria. Hacerlo tarde o depender de terceros supone un riesgo para tu asesoría y tus clientes.
            <br /><br />
            BOEResumido.com automatiza la lectura y el filtrado. Separamos el grano de la paja para que empieces el día informado y sin perder tiempo navegando por PDFs interminables.
          </p>
        </div>
      </section>

      {/* Included Items */}
      <section className="py-20 max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Información jurídica esencial</h2>
        </div>
        <div className="bg-white p-8 rounded-xl border border-blue-100 shadow-sm max-w-3xl mx-auto">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <SafeIcon icon={FiCheck} className="text-blue-700 w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Qué incluimos</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <ul className="space-y-4 text-gray-700 text-lg">
              <li className="flex items-start"><span className="mr-3 text-blue-600">•</span> Leyes Orgánicas y Ordinarias</li>
              <li className="flex items-start"><span className="mr-3 text-blue-600">•</span> Reales Decretos-ley y Legislativos</li>
            </ul>
            <ul className="space-y-4 text-gray-700 text-lg">
              <li className="flex items-start"><span className="mr-3 text-blue-600">•</span> Órdenes Ministeriales normativas</li>
              <li className="flex items-start"><span className="mr-3 text-blue-600">•</span> Modificaciones legislativas directas</li>
            </ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-blue-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">Directo a tu bandeja de entrada</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="text-4xl font-bold text-blue-300 mb-4">1</div>
              <h3 className="text-xl font-bold mb-3">Suscripción</h3>
              <p className="text-blue-100">Te apuntas con tu email. No pedimos nada más.</p>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-blue-300 mb-4">2</div>
              <h3 className="text-xl font-bold mb-3">Análisis</h3>
              <p className="text-blue-100">Nuestro sistema procesa el BOE cada madrugada y detecta las disposiciones generales relevantes.</p>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-blue-300 mb-4">3</div>
              <h3 className="text-xl font-bold mb-3">Envío</h3>
              <p className="text-blue-100">A las 08:00 recibes un correo con los titulares y un resumen accionable de cada norma.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Publico Objetivo */}
      <section className="py-20 max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">Diseñado para profesionales exigentes</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-6 border rounded-xl hover:shadow-md transition-shadow">
            <SafeIcon icon={FiShield} className="text-blue-800 w-8 h-8 mb-4" />
            <h3 className="font-bold text-xl mb-3">Abogados</h3>
            <p className="text-gray-600">Evita que un cliente te informe de un cambio legal antes de que tú lo hayas leído.</p>
          </div>
          <div className="p-6 border rounded-xl hover:shadow-md transition-shadow">
            <SafeIcon icon={FiFileText} className="text-blue-800 w-8 h-8 mb-4" />
            <h3 className="font-bold text-xl mb-3">Asesores Fiscales y Laborales</h3>
            <p className="text-gray-600">Anticípate a las publicaciones del BOE que impactan en las obligaciones de tus empresas clientes.</p>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 text-center px-6 bg-gray-50">
        <h2 className="text-3xl font-bold mb-8">La tranquilidad de estar al día en 2 minutos.</h2>
        <Link to="/register" className="inline-flex items-center text-lg bg-blue-800 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-900 transition-all hover:-translate-y-1 shadow-lg shadow-blue-100">
          <SafeIcon icon={FiMail} className="mr-3" />
          Suscribirme gratis
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 bg-white text-gray-500 text-sm mt-auto">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
             <div className="mb-4 md:mb-0 font-bold text-gray-700">BOEResumido.com</div>
             <div className="space-x-6">
                <Link to="/legal" className="hover:text-blue-800">Aviso Legal</Link>
                <Link to="/privacy" className="hover:text-blue-800">Privacidad</Link>
                <Link to="/terms" className="hover:text-blue-800">Términos</Link>
             </div>
          </div>
          
          <div className="border-t border-gray-100 pt-8 text-xs text-gray-400 text-justify leading-relaxed">
            <p className="mb-4">
              <strong>BOEResumido.com</strong> es un servicio privado e independiente, sin vinculación ni respaldo de la Administración Pública ni del Gobierno de España.
            </p>
            <p className="mb-4">
              El contenido se ofrece exclusivamente con fines informativos y de apoyo a la lectura del Boletín Oficial del Estado. Aunque nos esforzamos por garantizar la exactitud de los resúmenes, pueden existir errores u omisiones.
            </p>
            <p className="mb-4">
              <strong>IMPORTANTE:</strong> La única fuente oficial y jurídicamente válida es el texto publicado en el BOE (boe.es). Este servicio no constituye asesoramiento jurídico. En la medida permitida por la ley, BOEResumido.com no asume responsabilidad por decisiones adoptadas en base a estos resúmenes.
            </p>
            <p>
              © 2026 BOEResumido.com. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;